/**
 * 수강권 판매(구매) React Query 훅 (instructor-web)
 *
 * 백엔드 `instructorPurchaseTicket` / `getInstructorMemberTickets`
 * (requireCenterMembership)와 1:1 contract. 강사는 소속 센터의 회원에게
 * 이미 등록된 카탈로그 수강권을 판매(등록)하고 보유 수강권을 조회한다.
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";
import { isDemoMode } from "@/demo/demoMode";

// --- 백엔드 contract ---

interface InstructorPurchaseTicketRequest {
  centerName: string;
  memberName: string;
  memberMobile: string;
  ticketId: string;
}

interface InstructorPurchaseTicketResponse {
  success: true;
}

export interface PurchaseTicketParams {
  centerName: string;
  memberName: string;
  memberMobile: string;
  ticketId: string;
}

/**
 * 회원 보유 수강권 1건 (백엔드 `getInstructorMemberTickets` 응답 형태).
 *
 * top-level `members`는 클라이언트 직접 read 권한이 없어 백엔드 callable로 받는다.
 * `purchasedAt`은 서버에서 ISO string으로 직렬화된 값.
 */
export interface MemberTicket {
  title: string;
  type: "P" | "G";
  remainingCoupon: number;
  maxCoupon: number;
  isActive: boolean;
  /** ISO 8601 또는 정규화된 datetime string. 무제한 수강권은 부재. */
  expireAt?: string;
  /** ISO/정규화 datetime string. Studiomate 동기화본은 구매일 정보가 없어 부재할 수 있다. */
  purchasedAt?: string;
}

interface GetInstructorMemberTicketsRequest {
  centerOwnerUid: string;
  centerId: number;
  memberId: number;
}

interface GetInstructorMemberTicketsResponse {
  tickets: MemberTicket[];
}

/**
 * 회원 보유 수강권을 백엔드 callable로 조회한다.
 *
 * 격리: 백엔드가 호출자의 (centerOwnerUid, centerId) 멤버십을 검증하고, owner
 * 서브트리(`users/{ownerUid}/centers/{centerId}/members/{memberId}`)에서만 회원을
 * 찾으므로 소속 센터 회원만 조회된다.
 */
export async function getMemberTickets(
  centerOwnerUid: string,
  centerId: number,
  memberId: number,
): Promise<MemberTicket[]> {
  // demo 모드는 백엔드에 접근하지 않는다 (기존 firestore 서비스 패턴).
  if (isDemoMode()) return [];
  const fn = httpsCallable<GetInstructorMemberTicketsRequest, GetInstructorMemberTicketsResponse>(
    getFirebaseFunctions(),
    "getInstructorMemberTickets",
  );
  const result = await fn({ centerOwnerUid, centerId, memberId });
  return result.data.tickets;
}

/**
 * 수강권 판매(구매) mutation.
 *
 * 성공 시 호출자(모달의 onSuccess)에서 보유 수강권을 refetch한다.
 */
export function usePurchaseTicket() {
  return useMutation({
    mutationFn: async (params: PurchaseTicketParams) => {
      const fn = httpsCallable<InstructorPurchaseTicketRequest, InstructorPurchaseTicketResponse>(
        getFirebaseFunctions(),
        "instructorPurchaseTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 회원 수강권 환불·취소 (owner·admin 전용) contract ---

interface CancelMemberTicketRequest {
  centerId: number;
  memberId: number;
  /** getMemberTickets 직렬화 배열에서의 순번 */
  ticketIndex: number;
  /** 가드: 해당 항목의 title */
  expectedTitle: string;
  /** 가드(선택): 해당 항목의 purchasedAt(ISO) */
  expectedPurchasedAt?: string;
}

interface CancelMemberTicketResponse {
  success: true;
}

export interface CancelMemberTicketParams {
  centerId: number;
  memberId: number;
  ticketIndex: number;
  expectedTitle: string;
  expectedPurchasedAt?: string;
}

/**
 * 회원 보유 수강권 1건을 소프트 만료(비활성)한다. 백엔드 CF `expireMemberTicket`
 * (requireAdmin, 구 cancelMemberTicket)을 호출하므로 owner/admin만 성공한다.
 * 호출 전 페이지에서 role로 분기한다.
 *
 * 안정적 id가 없어 UI가 직렬화 배열 순번(ticketIndex)과 title/purchasedAt을 함께 보내고,
 * 백엔드가 동일 직렬화로 순번→raw 인덱스를 매핑 + 정합성 가드를 수행한다.
 */
export function useCancelMemberTicket() {
  return useMutation({
    mutationFn: async (params: CancelMemberTicketParams) => {
      const fn = httpsCallable<CancelMemberTicketRequest, CancelMemberTicketResponse>(
        getFirebaseFunctions(),
        "expireMemberTicket",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
